"use client";

import {
  JSX,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent as ReactTouchEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { cn } from "@/lib/utils";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { useDismiss } from "@/lib/hooks/use-dismiss";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import {
  useHoverSelect,
  useLegendHoverSelect,
  type HoverSelectSource,
} from "@/lib/hooks/use-hover-select";
import { fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { CascadeIn } from "@/components/motion/cascade-in";
import { useElementWidth } from "@/lib/hooks/use-element-width";
import { layoutBoxSize, layoutScale } from "@/lib/layout-size";
import { useTween } from "@/lib/hooks/use-tween";
import type {
  ModuleLogoWallData,
  ModuleLogoWallIndustry,
  ModuleLogoWallItem,
} from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import {
  RemoteImage,
  resolveLndevSlug,
} from "@/components/handyout-components/visual-ref";

const COPIES = 5;
const GAP = 8;

function resolveLogoSrc(logo: string): string {
  return logo.startsWith("lndev:") ? resolveLndevSlug(logo) : logo;
}

function polar(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r };
}

function donutArcPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const sweep = endAngle - startAngle;
  if (sweep <= 0) return "";
  if (sweep >= Math.PI * 2 - 1e-6) {
    const midAngle = startAngle + Math.PI;
    const o1 = polar(cx, cy, outerR, startAngle);
    const o2 = polar(cx, cy, outerR, midAngle);
    const i1 = polar(cx, cy, innerR, startAngle);
    const i2 = polar(cx, cy, innerR, midAngle);
    return [
      `M ${o1.x} ${o1.y}`,
      `A ${outerR} ${outerR} 0 1 1 ${o2.x} ${o2.y}`,
      `A ${outerR} ${outerR} 0 1 1 ${o1.x} ${o1.y}`,
      `M ${i1.x} ${i1.y}`,
      `A ${innerR} ${innerR} 0 1 0 ${i2.x} ${i2.y}`,
      `A ${innerR} ${innerR} 0 1 0 ${i1.x} ${i1.y}`,
      "Z",
    ].join(" ");
  }
  const largeArc = sweep > Math.PI ? 1 : 0;
  const o1 = polar(cx, cy, outerR, startAngle);
  const o2 = polar(cx, cy, outerR, endAngle);
  const i2 = polar(cx, cy, innerR, endAngle);
  const i1 = polar(cx, cy, innerR, startAngle);
  return [
    `M ${o1.x} ${o1.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${o2.x} ${o2.y}`,
    `L ${i2.x} ${i2.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${i1.x} ${i1.y}`,
    "Z",
  ].join(" ");
}

interface IndustryPieProps {
  industries: ModuleLogoWallIndustry[];
  counts: number[];
  activeIndex: number | null;
  onHoverChange: (index: number | null) => void;
  onSelect: (index: number, source?: HoverSelectSource) => void;
  size?: number;
}

const IndustryPie = memo(function IndustryPie({
  industries,
  counts,
  activeIndex,
  onHoverChange,
  onSelect,
  size = 108,
}: IndustryPieProps) {
  const total = counts.reduce((a, b) => a + b, 0);
  const hasHover = useHoverCapable();
  const { t } = useViewerLanguage();
  const cx = size / 2;
  const cy = size / 2;
  const baseOuter = size / 2 - 4;
  const innerR = baseOuter * 0.62;
  const gapAngle = 0.025;

  let acc = -Math.PI / 2;
  const slices = industries.map((ind, i) => {
    const value = counts[i] ?? 0;
    const sliceAngle = total > 0 ? (value / total) * Math.PI * 2 : 0;
    const start = acc + gapAngle / 2;
    const end = acc + sliceAngle - gapAngle / 2;
    acc += sliceAngle;
    return { industry: ind, index: i, value, start, end };
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0 overflow-visible"
      aria-hidden="true"
    >
      <title>{t("Customer distribution by industry")}</title>
      {slices.map(({ industry, index, value, start, end }) => {
        if (value <= 0) return null;
        const isActive = activeIndex === index;
        const isDimmed = activeIndex !== null && !isActive;
        const outerR = isActive ? baseOuter + 3 : baseOuter;
        const d = donutArcPath(cx, cy, outerR, innerR, start, end);
        return (
          <path
            key={industry.id}
            d={d}
            fill={industry.color}
            opacity={isDimmed ? 0.3 : 1}
            style={{ transition: "opacity 0.18s ease" }}
            onMouseEnter={hasHover ? () => onHoverChange(index) : undefined}
            onMouseLeave={hasHover ? () => onHoverChange(null) : undefined}
            onClick={() =>
              // The pie picks an industry, not a datum, so it filters like the
              // legend row it mirrors rather than counting as an element.
              onSelect(index, {
                kind: "slice",
                id: industry.id,
                origin: "legend",
              })
            }
            className="cursor-pointer"
          />
        );
      })}
      <text
        x={cx}
        y={cy - 3}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-[var(--fg)] font-medium tabular-nums"
        style={{ fontSize: 18 }}
      >
        {total}
      </text>
      <text
        x={cx}
        y={cy + 14}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-[var(--subtle)]"
        style={{ fontSize: 10 }}
      >
        {t("Customers")}
      </text>
    </svg>
  );
});

interface LogoMarqueeProps {
  items: ModuleLogoWallItem[];
  filter: string | null;
  onActiveChange?: (item: ModuleLogoWallItem | null) => void;
  speed: number;
  grayscale?: boolean;
}

const LogoMarquee = memo(function LogoMarquee({
  items,
  filter,
  onActiveChange,
  speed,
  grayscale,
}: LogoMarqueeProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const st = useRef<{
    pos: number;
    w: number;
    paused: boolean;
    pinned: boolean;
    resumeAt: number;
    drag: { x: number; sl: number; scale: number } | null;
    downX: number | null;
    moved: boolean;
  }>({
    pos: 0,
    w: 0,
    paused: false,
    pinned: false,
    resumeAt: 0,
    drag: null,
    downX: null,
    moved: false,
  });
  const logo = useHoverSelect<string>();
  const { setHovered, setSelected } = logo;
  const activeId = logo.effective;
  const hasHover = useHoverCapable();

  const shown = useMemo(
    () =>
      filter === null ? items : items.filter((l) => l.industry === filter),
    [items, filter],
  );

  // The strip scrolls itself, so a standing selection has to stop it: the
  // detail line below only makes sense while the tile it names is still under
  // the reader, and a second tap to clear needs the same tile to stay put.
  useEffect(() => {
    st.current.pinned = logo.selected !== null;
  }, [logo.selected]);

  const clearSelection = useCallback(() => setSelected(null), [setSelected]);
  useDismiss(logo.selected !== null, clearSelection, scrollRef);

  // A tile the filter just removed fires no mouseleave and can no longer be
  // tapped again to release it, so it would keep the strip pinned on nothing.
  useEffect(() => {
    const present = (id: string | null) =>
      id !== null && shown.some((l) => l.id === id);
    if (logo.selected !== null && !present(logo.selected)) setSelected(null);
    if (logo.hovered !== null && !present(logo.hovered)) setHovered(null);
  }, [logo.selected, logo.hovered, shown, setSelected, setHovered]);

  useEffect(() => {
    onActiveChange?.(shown.find((it) => it.id === activeId) ?? null);
  }, [activeId, shown, onActiveChange]);

  useEffect(() => {
    const sc = scrollRef.current;
    const cp = copyRef.current;
    if (!(sc && cp)) return;

    const measure = () => {
      const s = st.current;
      const first = !s.w;
      s.w = layoutBoxSize(cp, "width") + GAP;
      if (first) {
        s.pos = s.w;
        sc.scrollLeft = s.w;
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(cp);

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let last = performance.now();
    const tick = (t: number) => {
      const dt = Math.min(64, t - last);
      last = t;
      const s = st.current;
      if (
        !reduce &&
        !s.paused &&
        !s.pinned &&
        !s.drag &&
        t > s.resumeAt &&
        s.w > 0
      ) {
        s.pos += (dt / 1000) * speed;
        if (s.pos >= s.w * 2) s.pos -= s.w;
        sc.scrollLeft = s.pos;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [speed]);

  // A narrower strip leaves the standing scroll offset past the end of the new
  // content, and the loop width it wraps against is stale until the observer
  // catches up. Re-anchor on the second copy, the same place the first measure
  // starts from.
  useEffect(() => {
    const sc = scrollRef.current;
    const cp = copyRef.current;
    if (!(sc && cp)) return;
    const s = st.current;
    s.w = layoutBoxSize(cp, "width") + GAP;
    s.pos = s.w;
    sc.scrollLeft = s.w;
    s.drag = null;
    s.downX = null;
    s.moved = false;
  }, [filter]);

  const pause = (ms: number) => {
    st.current.resumeAt = performance.now() + ms;
  };

  const onScroll = () => {
    const s = st.current;
    const sc = scrollRef.current;
    if (!(sc && s.w)) return;
    let x = sc.scrollLeft;
    let shift = 0;
    if (x < s.w * 0.5) shift = s.w;
    else if (x >= s.w * 2.5) shift = -s.w;
    if (shift) {
      sc.scrollLeft = x + shift;
      x += shift;
      if (s.drag) s.drag.sl += shift;
    }
    if (Math.abs(x - s.pos) > 2) s.pos = x;
  };

  const tile = (l: ModuleLogoWallItem, copy: number, index: number) => {
    const isActive = activeId === l.id;
    const dim = activeId !== null && !isActive;
    return (
      <div
        key={`${copy}-${l.id}`}
        onMouseEnter={hasHover ? () => logo.setHovered(l.id) : undefined}
        onMouseLeave={hasHover ? () => logo.setHovered(null) : undefined}
        onClick={(e) => {
          if (st.current.moved) return;
          logo.select(l.id, {
            kind: "card",
            id: l.id,
            index,
            element: e.currentTarget,
          });
        }}
        className={cn(
          "flex shrink-0 flex-col items-center gap-2 rounded-lg border border-dashed p-3 transition-all",
          isActive
            ? "border-black/[0.18] bg-black/[0.03] dark:border-white/[0.30] dark:bg-white/[0.06]"
            : "border-black/[0.07] dark:border-white/[0.12]",
          dim && "opacity-35",
        )}
      >
        <RemoteImage
          src={resolveLogoSrc(l.logo)}
          alt={l.name}
          className={cn("h-7 w-7", grayscale && "grayscale")}
          fallbackClassName="h-7 w-7"
        />
        <span
          className={cn(
            "text-center text-[13px] font-medium leading-tight tracking-tight",
            MODULE_LABEL_WRAP.caption,
            isActive ? "text-[var(--fg)]" : "text-[var(--muted)]",
          )}
        >
          {stripInlineMarkdown(l.name)}
        </span>
      </div>
    );
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    st.current.downX = e.clientX;
    st.current.moved = false;
    if (e.pointerType !== "mouse" || !scrollRef.current) {
      pause(2500);
      return;
    }
    st.current.drag = {
      x: e.clientX,
      sl: scrollRef.current.scrollLeft,
      scale: layoutScale(scrollRef.current),
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = st.current;
    // A drag or swipe still ends in a click on whatever tile was under the
    // pointer, so the tap path has to know the gesture moved.
    if (s.downX !== null && Math.abs(e.clientX - s.downX) > 6) s.moved = true;
    const d = s.drag;
    if (!(d && scrollRef.current)) return;
    scrollRef.current.scrollLeft = d.sl - (e.clientX - d.x) / d.scale;
  };

  const onPointerUp = () => {
    st.current.drag = null;
    st.current.downX = null;
    pause(1500);
  };

  const cascadeKey = `logo-wall-${filter ?? "all"}-${shown.length}`;

  const onWheel = (_: ReactWheelEvent<HTMLDivElement>) => pause(1800);
  const onTouchStart = (_: ReactTouchEvent<HTMLDivElement>) => pause(2500);
  const onTouchMove = (_: ReactTouchEvent<HTMLDivElement>) => pause(2500);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="cursor-grab select-none overflow-x-auto active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={onScroll}
        onMouseEnter={
          hasHover
            ? () => {
                st.current.paused = true;
              }
            : undefined
        }
        onMouseLeave={
          hasHover
            ? () => {
                st.current.paused = false;
                pause(600);
              }
            : undefined
        }
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="flex w-max py-0.5" style={{ gap: GAP }}>
          {Array.from({ length: COPIES }).map((_, k) => (
            <div
              key={k}
              ref={k === 0 ? copyRef : undefined}
              aria-hidden={k > 0 ? true : undefined}
              className="flex"
              style={{ gap: GAP }}
            >
              {/* display:contents keeps the tiles direct flex children, so neither the marquee gap nor the copy-width measurement changes. */}
              <CascadeIn
                keySignature={cascadeKey}
                className="contents"
                itemClassName="shrink-0"
                staggerMs={10}
                delayMs={60}
                duration={0.26}
                blur={4}
              >
                {shown.map((l, i) => tile(l, k, i))}
              </CascadeIn>
            </div>
          ))}
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-16"
        style={{
          background: "linear-gradient(to right, var(--card), transparent)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-16"
        style={{
          background: "linear-gradient(to left, var(--card), transparent)",
        }}
      />
    </div>
  );
});

export default function ModuleLogoWall({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleLogoWallData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const logos = data.logos;
  const industries = data.industries;
  const total = data.total ?? 500;
  const totalSuffix = data.totalSuffix ?? "+";
  const totalLabel = data.totalLabel ?? t("companies");
  const delta = data.delta;
  const speed = data.speed ?? 30;
  const idleHint = data.idleHint ?? t("Drag or swipe to browse");

  const [ref, width] = useElementWidth<HTMLDivElement>();
  const compact = width > 0 && width < 600;
  const legend = useLegendHoverSelect();
  const { setSelected: setLegendSelected } = legend;
  const legendRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const clearIndustry = useCallback(
    () => setLegendSelected(null),
    [setLegendSelected],
  );
  // The strip carries the filtered set, so a tap in it must not drop the
  // industry: the items would come back and move out from under the finger
  // before the tap they were aimed at lands.
  const insideMarquee = useCallback(
    (target: Element) => Boolean(marqueeRef.current?.contains(target)),
    [],
  );
  useDismiss(legend.selected !== null, clearIndustry, legendRef, {
    ignore: insideMarquee,
  });
  const hasHover = useHoverCapable();
  const [activeLogo, setActiveLogo] = useState<ModuleLogoWallItem | null>(
    null,
  );
  const count = useTween(total, { from: 0, duration: 1100 });

  const industryById = useMemo(
    () =>
      Object.fromEntries(industries.map((i) => [i.id, i])) as Record<
        string,
        ModuleLogoWallIndustry
      >,
    [industries],
  );

  const industryCounts = useMemo(
    () =>
      industries.map((i) => logos.filter((l) => l.industry === i.id).length),
    [industries, logos],
  );

  const activeIndustry = legend.effective;
  // Membership follows the pinned industry alone. `legend.filter` would include
  // the live hover, and rebuilding the strip under the pointer on hover costs
  // the reader their scroll position.
  const pinnedIndustry =
    legend.selectedOrigin === "legend" ? legend.selected : null;
  const filter =
    pinnedIndustry !== null ? industries[pinnedIndustry]?.id ?? null : null;

  return (
    <div
      ref={ref}
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div
        className={cn(
          "flex gap-5",
          compact ? "flex-col" : "items-end justify-between",
        )}
      >
        <div>
          <div className="text-5xl font-medium tracking-tight tabular-nums">
            {fmtNum(Math.round(count), 0, intlLocale)}
            {totalSuffix}
          </div>
          <div
            className={cn("mt-1.5 text-sm text-[var(--muted)]", MODULE_LABEL_WRAP.label)}
          >
            {stripInlineMarkdown(totalLabel)}
          </div>
          {delta && (
            <div
              className={cn("mt-1 text-sm text-[var(--muted)]", MODULE_LABEL_WRAP.row)}
            >
              <span className="underline decoration-dotted underline-offset-4">
                {stripInlineMarkdown(delta)}
              </span>
            </div>
          )}
        </div>
        <div
          className={cn(
            "flex items-center gap-4",
            compact && "self-start",
          )}
          ref={legendRef}
        >
          <IndustryPie
            industries={industries}
            counts={industryCounts}
            activeIndex={activeIndustry}
            onHoverChange={legend.setHovered}
            onSelect={legend.select}
          />
          <div className="flex flex-col gap-1">
            {industries.map((ind, i) => {
              const val = industryCounts[i] ?? 0;
              const isActive = activeIndustry === i;
              const isDimmed = activeIndustry !== null && !isActive;
              return (
                <button
                  key={ind.id}
                  type="button"
                  ref={legend.setLabelRef(i)}
                  onMouseEnter={hasHover ? () => legend.setHovered(i) : undefined}
                  onMouseLeave={
                    hasHover ? () => legend.setHovered(null) : undefined
                  }
                  onFocus={hasHover ? () => legend.setHovered(i) : undefined}
                  onBlur={hasHover ? () => legend.setHovered(null) : undefined}
                  onClick={() =>
                    legend.select(i, { kind: "legend-item", id: ind.id })
                  }
                  className={cn(
                    "flex items-center gap-2 rounded-sm px-1 py-0.5 text-left transition-opacity",
                    isDimmed && "opacity-40",
                  )}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: ind.color }}
                  />
                  <span className="whitespace-nowrap text-xs text-[var(--fg)]">
                    {stripInlineMarkdown(ind.label)}
                  </span>
                  <span className="ml-auto text-xs tabular-nums text-[var(--subtle)]">
                    {val}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        ref={marqueeRef}
        className="mt-5 border-t border-dashed border-[var(--border-dashed-soft)] pt-5"
      >
        <LogoMarquee
          items={logos}
          filter={filter}
          onActiveChange={setActiveLogo}
          speed={speed}
          grayscale={grayscale}
        />
      </div>

      <div className="mt-4 h-5 text-sm">
        {activeLogo ? (
          <span className="text-[var(--muted)]">
            <span
              className={cn(
                "font-medium text-[var(--fg)]",
                MODULE_LABEL_WRAP.label,
              )}
            >
              {stripInlineMarkdown(activeLogo.name)}
            </span>
            {industryById[activeLogo.industry] && (
              <> · {industryById[activeLogo.industry].label}</>
            )}
            {activeLogo.since && (
              <> · {t("Customer since {year}", { year: String(activeLogo.since) })}</>
            )}
          </span>
        ) : (
          <span className={cn("text-[var(--subtle)]", MODULE_LABEL_WRAP.body)}>
            {idleHint}
          </span>
        )}
      </div>
    </div>
  );
}
